import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Select, MenuItem,
  FormControl, InputLabel, Stack, Alert, CircularProgress, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Autocomplete, Divider, Chip, Tabs, Tab, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Save as SaveIcon,
  History as HistoryIcon,
  NoteAdd as NoteAddIcon,
  AttachMoney as MoneyIcon,
  Edit as EditIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { apiService, urlDeArchivo } from '../../services/api';

interface InvoiceItem {
  description: string;
  quantity: number;
  unitPrice: number;
  // Vínculo con el equipo que se está cobrando. Viaja hasta la factura guardada para que el
  // año que viene se sepa qué cubrió ésta, en vez de deducirlo por mes de vencimiento.
  deviceInfo?: { deviceId: string; imei?: string; brand?: string; model?: string };
}

// Lo que quedó fuera de la factura y hay que ver antes de emitirla.
interface AvisoRenovacion {
  cliente: string;
  imei: string;
  name?: string;
  expirationTime?: string;
  reason?: string;
}

interface ClientOption {
  _id: string;
  name: string;
  email: string;
  address?: { street?: string; city?: string };
}

interface SavedInvoice {
  _id: string;
  invoiceNumber: string;
  invoiceType: string;
  total: number;
  status: string;
  issueDate: string;
  clientId?: { name?: string };
  items?: any[];
  notes?: string;
  terms?: string;
  registeredInCash?: boolean;
  paidDate?: string;
  paymentInfo?: { method?: string; transactionId?: string; evidenceUrl?: string };
}

const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const TYPE_LABELS: Record<string, string> = {
  installation: 'Instalacion',
  renewal: 'Renovacion',
  annual_service: 'Servicio Anual',
  additional_service: 'Servicio Adicional',
  repair: 'Reparacion',
};

const METODO_PAGO: Record<string, string> = {
  cash: 'efectivo',
  bank_transfer: 'transferencia',
  check: 'cheque',
};

const STATUS_COLORS: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  draft: 'default',
  issued: 'info',
  sent: 'info',
  paid: 'success',
  overdue: 'error',
  cancelled: 'default',
};

const InvoiceGeneratorPage: React.FC = () => {
  const now = new Date();
  const [tab, setTab] = useState(0);

  // Generator state
  const [invoiceType, setInvoiceType] = useState<'installations' | 'renewals'>('installations');
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [selectedClient, setSelectedClient] = useState<ClientOption | null>(null);
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [items, setItems] = useState<InvoiceItem[]>([]);
  // Equipos que el generador dejó fuera de la factura, y los que vencen unos días después
  // del mes. Se muestran aparte: sin esto, un equipo que no entra desaparece sin dejar
  // rastro y nadie se entera hasta que falta el cobro.
  const [excluidos, setExcluidos] = useState<AvisoRenovacion[]>([]);
  const [proximos, setProximos] = useState<AvisoRenovacion[]>([]);
  const [diasDeGracia, setDiasDeGracia] = useState(0);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [note, setNote] = useState('');
  const [bankInfo, setBankInfo] = useState(
    'No. de Cuenta: 1680010384 | Nombre: Maclofin Olivo Burgos | Tipo: Cuenta de Ahorro | Metodo de pago: BanReservas'
  );
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  // La factura guardada sobre la que se está trabajando. Mientras exista, todo lo que se haga
  // en esta pantalla modifica ESA factura en vez de emitir otra: se corrige, se descarga las
  // veces que haga falta y se cierra con el pago. Una sola factura por cobro.
  const [facturaId, setFacturaId] = useState<string | null>(null);
  const [facturaEstado, setFacturaEstado] = useState<string>('');
  const [guardandoCambios, setGuardandoCambios] = useState(false);
  const [dialogoPago, setDialogoPago] = useState(false);
  const [formaDePago, setFormaDePago] = useState({ method: 'cash', transactionId: '' });
  const [recibo, setRecibo] = useState<File | null>(null);
  const [cobrando, setCobrando] = useState(false);
  const [borrando, setBorrando] = useState<string | null>(null);

  // History state
  const [savedInvoices, setSavedInvoices] = useState<SavedInvoice[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyFilter, setHistoryFilter] = useState('');

  useEffect(() => {
    apiService.getClients(1, 500).then((res: any) => {
      const list = res?.data?.clients || res?.clients || [];
      setClients(list);
    }).catch(() => {});
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const params: any = { limit: 50 };
      if (historyFilter) params.search = historyFilter;
      const result = await apiService.getInvoices(1, 50, params);
      setSavedInvoices(result?.invoices || []);
    } catch {
      // silent
    } finally {
      setHistoryLoading(false);
    }
  }, [historyFilter]);

  // No hay atajo para meter una factura a caja sin cobrarla. Existió un botón de "ya estaba
  // cobrada" y duró una tarde: era tan fácil de tocar que dos facturas —RD$60,500 entre las
  // dos— entraron a la contabilidad por curiosidad, y de caja no se sale. Si se cobró por
  // fuera, se registra el cobro igual, con su método y su comprobante.

  useEffect(() => {
    if (tab === 1) loadHistory();
  }, [tab, loadHistory]);

  const fetchNextNumber = async (clientId: string) => {
    try {
      const result = await apiService.getNextInvoiceNumber(clientId);
      setInvoiceNumber(result.invoiceNumber);
    } catch {
      // fallback
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    setError(null);
    setSaved(false);
    setFacturaId(null);
    setFacturaEstado('');
    setExcluidos([]);
    setProximos([]);
    try {
      const clientId = selectedClient?._id;
      let result: any;

      if (invoiceType === 'installations') {
        result = await apiService.getInstallationsForInvoice(year, month, clientId);
      } else {
        result = await apiService.getRenewalsForInvoice(year, month, clientId);
      }

      const data = result?.data;
      if (!data?.clients?.length) {
        setItems([]);
        setError(`No se encontraron ${invoiceType === 'installations' ? 'instalaciones' : 'renovaciones'} para ${MONTHS[month - 1]} ${year}`);
        return;
      }

      const allItems: InvoiceItem[] = [];
      const fuera: AvisoRenovacion[] = [];
      const cerca: AvisoRenovacion[] = [];
      data.clients.forEach((c: any) => {
        c.items.forEach((item: any) => {
          allItems.push({
            description: item.description,
            quantity: item.quantity || 1,
            unitPrice: item.unitPrice || 0,
            ...(item.deviceInfo?.deviceId ? { deviceInfo: item.deviceInfo } : {}),
          });
        });
        (c.excluded || []).forEach((e: any) => fuera.push({ ...e, cliente: c.client?.name || '' }));
        (c.nearby || []).forEach((n: any) => cerca.push({ ...n, cliente: c.client?.name || '' }));
      });

      setItems(allItems);
      setExcluidos(fuera);
      setProximos(cerca);
      setDiasDeGracia(data.graceDays || 0);

      let resolvedClient = selectedClient;
      if (!selectedClient && data.clients.length === 1 && data.clients[0].client) {
        const c = data.clients[0].client;
        resolvedClient = { _id: c._id, name: c.name, email: c.email, address: c.address };
        setSelectedClient(resolvedClient);
      }

      if (resolvedClient?._id) {
        await fetchNextNumber(resolvedClient._id);
      }

      const typeLabel = invoiceType === 'installations' ? 'Instalaciones' : 'Renovaciones';
      // La nota al pie del asterisco se pega a la nota de la factura para que salga impresa
      // en el PDF: marcar renglones y no explicar el asterisco confunde al dealer.
      const alPie = data.footnote ? `\n\n${data.footnote}` : '';
      setNote(`${typeLabel} de ${MONTHS[month - 1]} ${year}${alPie}`);
    } catch (err: any) {
      setError(err.message || 'Error buscando datos');
    } finally {
      setLoading(false);
    }
  };

  const handleItemChange = (index: number, field: keyof InvoiceItem, value: any) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [field]: value };
    setItems(updated);
  };

  const addItem = () => {
    setItems([...items, { description: '', quantity: 1, unitPrice: 0 }]);
  };

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const total = items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
  // Una factura pagada no se toca: el backend la bloquea y aquí se refleja para que no se
  // intente y falle.
  const pagada = facturaEstado === 'paid';

  const downloadPdf = async (pdfItems: InvoiceItem[], pdfNumber: string, clientName: string, clientAddr: string, pdfNote: string, pdfBank: string) => {
    // La fecha de la factura refleja el mes/año seleccionado (al que pertenece), no hoy.
    const lastDay = new Date(year, month, 0);
    const invoiceDate = lastDay.toLocaleDateString('es-DO');
    const blob = await apiService.generateInvoicePdf({
      invoiceNumber: pdfNumber,
      invoiceDate,
      dueDate: 'N/A',
      clientName,
      clientAddress: clientAddr,
      contactName: clientName,
      contactAddress: clientAddr,
      items: pdfItems,
      note: pdfNote,
      bankInfo: pdfBank,
    });
    const url = window.URL.createObjectURL(blob);
    window.open(url, '_blank');
  };

  // Descarga el PDF como BORRADOR sin guardar la factura ni tocar la caja.
  // Úsalo para mandarle al dealer la propuesta con todas las renovaciones; cuando él
  // confirme cuáles, quitas los ítems que no van y recién ahí usas "Guardar y Generar".
  const handleDownloadDraft = async () => {
    if (!items.length) return;
    setGenerating(true);
    setError(null);
    try {
      const clientAddr = selectedClient?.address;
      const address = clientAddr ? `${clientAddr.street || ''}, ${clientAddr.city || ''}` : '';
      await downloadPdf(items, invoiceNumber, selectedClient?.name || '', address, note, bankInfo);
      setSuccess('Borrador descargado (NO se guardó ni entró a caja). Cuando sea la definitiva, usa "Guardar y Generar".');
      setTimeout(() => setSuccess(null), 7000);
    } catch (err: any) {
      setError(err.message || 'Error generando el borrador');
    } finally {
      setGenerating(false);
    }
  };

  const handleGeneratePdf = async () => {
    if (!items.length) return;
    setGenerating(true);
    setError(null);
    try {
      if (!saved && selectedClient?._id) {
        try {
          const guardada: any = await apiService.saveInvoice({
            clientId: selectedClient._id,
            invoiceType: invoiceType === 'installations' ? 'installation' : 'renewal',
            items,
            note,
            bankInfo,
            invoiceNumber,
            // La factura pertenece al mes/año elegido (el de las instalaciones/renovaciones),
            // no al día de hoy → así el efectivo suma en ese mes en el reporte financiero.
            year,
            month,
          });
          setSaved(true);
          if (guardada?.invoice?._id) {
            setFacturaId(guardada.invoice._id);
            setFacturaEstado(guardada.invoice.status || 'issued');
          }
        } catch (saveErr: any) {
          const msg = saveErr?.response?.data?.message || saveErr.message;
          // Ya hay una factura de ese cliente para ese mes. No se emite otra: se ofrece
          // abrir la que existe, que es lo que evita terminar con tres del mismo cobro.
          const yaHay = saveErr?.response?.data?.existing;
          if (saveErr?.response?.status === 409 && yaHay) {
            setError(msg);
            setTab(1);
            loadHistory();
            setGenerating(false);
            return;
          }
          if (!msg?.includes('ya existe')) {
            setError(`Error guardando: ${msg}`);
            setGenerating(false);
            return;
          }
          setSaved(true);
        }
      }

      const clientAddr = selectedClient?.address;
      const address = clientAddr ? `${clientAddr.street || ''}, ${clientAddr.city || ''}` : '';
      await downloadPdf(items, invoiceNumber, selectedClient?.name || '', address, note, bankInfo);

      setSuccess(`Factura ${invoiceNumber} generada y guardada`);
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      setError(err.message || 'Error generando PDF');
    } finally {
      setGenerating(false);
    }
  };

  // Guardar correcciones sobre la factura que ya existe, sin emitir otra.
  const guardarCambios = async () => {
    if (!facturaId) return;
    setGuardandoCambios(true);
    setError(null);
    try {
      await apiService.updateInvoice(facturaId, { items, notes: note, terms: bankInfo });
      setSuccess(`Factura ${invoiceNumber} actualizada`);
      setTimeout(() => setSuccess(null), 4000);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'No se pudieron guardar los cambios');
    } finally {
      setGuardandoCambios(false);
    }
  };

  // Cerrar con el pago. El backend deja de permitir editarla a partir de aquí, y por eso
  // se avisa antes en el diálogo en vez de descubrirlo con un error después.
  const cerrarConPago = async () => {
    if (!facturaId) return;
    setCobrando(true);
    setError(null);
    try {
      const res: any = await apiService.payInvoice(facturaId, {
        method: formaDePago.method,
        transactionId: formaDePago.transactionId || undefined,
        receipt: recibo,
      });
      setFacturaEstado('paid');
      setDialogoPago(false);
      setRecibo(null);
      // Cobrar una factura de renovación renueva los equipos sola. Se dice cuántos, y sobre
      // todo cuáles NO pudieron renovarse: esos hay que hacerlos a mano y si no se avisan,
      // el cron los mata igual aunque el cliente haya pagado.
      const rn = res?.renovacion;
      const pendientes = [...(rn?.fallidos || []), ...(rn?.omitidos || [])];
      if (rn && pendientes.length > 0) {
        setError(
          `Factura cobrada, pero ${pendientes.length} equipo(s) no se renovaron solos: ` +
          pendientes.map((f: any) => `${f.name || f.imei || f.descripcion} (${f.motivo})`).join('; ') +
          '. Renuévalos a mano desde Renovaciones.'
        );
      }
      setSuccess(
        rn
          ? `Factura ${invoiceNumber} cobrada y cerrada. Se renovaron ${rn.renovados?.length || 0} equipo(s).`
          : `Factura ${invoiceNumber} cobrada, cerrada y contabilizada en caja`
      );
      setTimeout(() => setSuccess(null), 5000);
      loadHistory();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'No se pudo registrar el pago');
    } finally {
      setCobrando(false);
    }
  };

  const borrarFactura = async (inv: SavedInvoice) => {
    const ok = window.confirm(
      `¿Borrar la factura ${inv.invoiceNumber} de ${inv.clientId?.name || 'ese cliente'}?\n\n` +
      `Son RD$${(inv.total || 0).toLocaleString('es-DO')}. Esto no se puede deshacer.`
    );
    if (!ok) return;
    setBorrando(inv._id);
    setError(null);
    try {
      await apiService.deleteInvoice(inv._id);
      if (facturaId === inv._id) { setFacturaId(null); setFacturaEstado(''); setSaved(false); }
      setSuccess(`Factura ${inv.invoiceNumber} eliminada`);
      setTimeout(() => setSuccess(null), 4000);
      loadHistory();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'No se pudo borrar la factura');
    } finally {
      setBorrando(null);
    }
  };

  // Abrir una factura del historial para seguir trabajándola aquí mismo.
  const abrirFactura = (inv: SavedInvoice) => {
    setItems((inv.items || []).map((it: any) => ({
      description: it.description,
      quantity: it.quantity || 1,
      unitPrice: it.unitPrice || 0,
      ...(it.deviceInfo?.deviceId ? { deviceInfo: it.deviceInfo } : {}),
    })));
    setInvoiceNumber(inv.invoiceNumber);
    setNote(inv.notes || '');
    setBankInfo(inv.terms || bankInfo);
    setInvoiceType(inv.invoiceType === 'installation' ? 'installations' : 'renewals');
    const cliente = clients.find(c => c.name === inv.clientId?.name);
    setSelectedClient(cliente || (inv.clientId ? { _id: '', name: inv.clientId.name || '', email: '' } : null));
    setFacturaId(inv._id);
    setFacturaEstado(inv.status);
    setSaved(true);
    setExcluidos([]);
    setProximos([]);
    setError(null);
    setTab(0);
  };

  // Cobrar desde el historial, sin tener que abrir la factura y buscar el botón. Se carga la
  // factura primero porque el diálogo cobra sobre `facturaId` y muestra su total.
  const cobrarDesdeLista = (inv: SavedInvoice) => {
    abrirFactura(inv);
    setFormaDePago({ method: 'cash', transactionId: '' });
    setRecibo(null);
    setDialogoPago(true);
  };

  const handleRedownload = async (inv: SavedInvoice) => {
    try {
      const pdfItems = (inv.items || []).map((it: any) => ({
        description: it.description,
        quantity: it.quantity || 1,
        unitPrice: it.unitPrice || 0,
      }));
      await downloadPdf(
        pdfItems,
        inv.invoiceNumber,
        inv.clientId?.name || '',
        '',
        inv.notes || '',
        inv.terms || bankInfo
      );
    } catch (err: any) {
      setError(err.message || 'Error descargando PDF');
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>Facturas</Typography>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab icon={<NoteAddIcon />} iconPosition="start" label="Generar Factura" />
        <Tab icon={<HistoryIcon />} iconPosition="start" label="Facturas Guardadas" />
      </Tabs>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* =================== TAB 0: GENERATOR =================== */}
      {tab === 0 && (
        <>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                <FormControl sx={{ minWidth: 180 }}>
                  <InputLabel>Tipo</InputLabel>
                  <Select value={invoiceType} label="Tipo" onChange={(e) => setInvoiceType(e.target.value as any)}>
                    <MenuItem value="installations">Instalaciones</MenuItem>
                    <MenuItem value="renewals">Renovaciones</MenuItem>
                  </Select>
                </FormControl>

                <FormControl sx={{ minWidth: 150 }}>
                  <InputLabel>Mes</InputLabel>
                  <Select value={month} label="Mes" onChange={(e) => setMonth(Number(e.target.value))}>
                    {MONTHS.map((m, i) => (
                      <MenuItem key={i} value={i + 1}>{m}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label="Ano"
                  type="number"
                  value={year}
                  onChange={(e) => setYear(Number(e.target.value))}
                  sx={{ width: 100 }}
                />

                <Autocomplete
                  options={clients}
                  getOptionLabel={(opt) => opt.name}
                  value={selectedClient}
                  onChange={(_, val) => setSelectedClient(val)}
                  renderInput={(params) => <TextField {...params} label="Cliente (opcional)" />}
                  sx={{ minWidth: 250, flexGrow: 1 }}
                  isOptionEqualToValue={(opt, val) => opt._id === val._id}
                />

                <Button
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                  onClick={handleSearch}
                  disabled={loading}
                  sx={{ height: 56 }}
                >
                  Buscar
                </Button>
              </Stack>
            </CardContent>
          </Card>

          {(excluidos.length > 0 || proximos.length > 0) && (
            <Card sx={{ mb: 3, borderLeft: '4px solid', borderColor: 'warning.main' }}>
              <CardContent>
                {excluidos.length > 0 && (
                  <Box sx={{ mb: proximos.length > 0 ? 2 : 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {excluidos.length} equipo{excluidos.length > 1 ? 's' : ''} vencen este mes pero no entran en la factura
                    </Typography>
                    {excluidos.map((e) => (
                      <Typography key={e.imei} variant="body2" color="text.secondary">
                        {e.imei}{e.name ? ` — ${e.name}` : ''}{e.cliente ? ` · ${e.cliente}` : ''} — {e.reason}
                      </Typography>
                    ))}
                  </Box>
                )}
                {proximos.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {proximos.length} equipo{proximos.length > 1 ? 's vencen' : ' vence'} dentro de los {diasDeGracia} días siguientes al mes
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      Un lote instalado en varios días parte su vencimiento entre dos meses. Si son
                      del mismo grupo, agrégalos a mano abajo para cobrarlos juntos.
                    </Typography>
                    {proximos.map((n) => (
                      <Typography key={n.imei} variant="body2" color="text.secondary">
                        {n.expirationTime ? new Date(n.expirationTime).toLocaleDateString('es-DO') : ''} · {n.imei}
                        {n.name ? ` — ${n.name}` : ''}{n.cliente ? ` · ${n.cliente}` : ''}
                      </Typography>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {items.length > 0 && (
            <>
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
                    <TextField
                      label="No. Factura"
                      value={invoiceNumber}
                      onChange={(e) => setInvoiceNumber(e.target.value)}
                      placeholder="PI-0001"
                      sx={{ width: 200 }}
                      InputProps={{ readOnly: saved }}
                    />
                    {saved && (
                      <Chip label="Guardada" color="success" variant="outlined" sx={{ alignSelf: 'center' }} />
                    )}
                    <TextField
                      label="Nota"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      sx={{ flexGrow: 1 }}
                    />
                  </Stack>

                  <Divider sx={{ mb: 2 }} />

                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#1565C0' }}>
                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Descripcion</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600, width: 100 }} align="center">Unidades</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600, width: 140 }} align="center">Precio Unitario</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600, width: 120 }} align="right">Total</TableCell>
                          <TableCell sx={{ color: 'white', width: 50 }} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {items.map((item, i) => (
                          <TableRow key={i} sx={{ '&:nth-of-type(even)': { backgroundColor: '#F5F5F5' } }}>
                            <TableCell>
                              <TextField
                                value={item.description}
                                onChange={(e) => handleItemChange(i, 'description', e.target.value)}
                                variant="standard"
                                fullWidth
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                value={item.quantity}
                                onChange={(e) => handleItemChange(i, 'quantity', Number(e.target.value))}
                                variant="standard"
                                type="number"
                                size="small"
                                sx={{ width: 60 }}
                                inputProps={{ min: 1, style: { textAlign: 'center' } }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                value={item.unitPrice}
                                onChange={(e) => handleItemChange(i, 'unitPrice', Number(e.target.value))}
                                variant="standard"
                                type="number"
                                size="small"
                                sx={{ width: 100 }}
                                inputProps={{ min: 0, style: { textAlign: 'center' } }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography fontWeight={500}>${(item.quantity * item.unitPrice).toLocaleString()}</Typography>
                            </TableCell>
                            <TableCell>
                              <IconButton size="small" color="error" onClick={() => removeItem(i)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
                    <Button startIcon={<AddIcon />} onClick={addItem} size="small">
                      Agregar Item
                    </Button>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#1565C0' }}>
                        TOTAL: ${total.toLocaleString()}
                      </Typography>
                      <Chip label={`${items.length} items`} size="small" sx={{ mt: 0.5 }} />
                    </Box>
                  </Stack>

                  <Divider sx={{ my: 2 }} />

                  <TextField
                    label="Datos bancarios / Instrucciones de pago"
                    value={bankInfo}
                    onChange={(e) => setBankInfo(e.target.value)}
                    fullWidth
                    multiline
                    rows={2}
                    size="small"
                  />
                </CardContent>
              </Card>

              {facturaId && (
                <Alert
                  severity={pagada ? 'success' : 'info'}
                  sx={{ mb: 2 }}
                  action={
                    pagada ? undefined : (
                      <Button color="inherit" size="small" onClick={() => setDialogoPago(true)}>
                        Cerrar con pago
                      </Button>
                    )
                  }
                >
                  {pagada
                    ? `Factura ${invoiceNumber} cerrada y pagada. Ya no se puede editar; descárgala las veces que necesites.`
                    : `Trabajando sobre la factura ${invoiceNumber}. Corrige lo que haga falta y guarda los cambios sobre ella misma — no se emite otra.`}
                </Alert>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<PdfIcon />}
                  onClick={handleDownloadDraft}
                  disabled={generating || items.length === 0}
                  sx={{ px: 3 }}
                  title="Descarga el PDF para enviárselo al dealer, SIN guardar la factura ni ponerla en caja"
                >
                  Descargar borrador
                </Button>

                {facturaId && !pagada && (
                  <Button
                    variant="outlined"
                    size="large"
                    color="primary"
                    startIcon={guardandoCambios ? <CircularProgress size={20} /> : <SaveIcon />}
                    onClick={guardarCambios}
                    disabled={guardandoCambios || items.length === 0}
                    sx={{ px: 3 }}
                  >
                    {guardandoCambios ? 'Guardando...' : 'Guardar cambios'}
                  </Button>
                )}

                {facturaId && !pagada && (
                  <Button
                    variant="contained"
                    size="large"
                    color="success"
                    startIcon={<MoneyIcon />}
                    onClick={() => setDialogoPago(true)}
                    sx={{ px: 3 }}
                  >
                    Cerrar con pago
                  </Button>
                )}

                <Button
                  variant="contained"
                  size="large"
                  startIcon={generating ? <CircularProgress size={20} color="inherit" /> : saved ? <PdfIcon /> : <SaveIcon />}
                  onClick={handleGeneratePdf}
                  disabled={generating || items.length === 0}
                  sx={{ px: 4 }}
                >
                  {generating ? 'Generando...' : saved ? 'Descargar PDF' : 'Guardar y Generar (definitiva)'}
                </Button>
              </Box>
            </>
          )}
        </>
      )}

      {/* =================== TAB 1: SAVED INVOICES =================== */}
      {tab === 1 && (
        <Card>
          <CardContent>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
              <TextField
                label="Buscar por numero"
                value={historyFilter}
                onChange={(e) => setHistoryFilter(e.target.value)}
                size="small"
                sx={{ width: 250 }}
                onKeyDown={(e) => e.key === 'Enter' && loadHistory()}
              />
              <Button
                variant="outlined"
                startIcon={historyLoading ? <CircularProgress size={18} /> : <SearchIcon />}
                onClick={loadHistory}
                disabled={historyLoading}
              >
                Buscar
              </Button>
            </Stack>

            {historyLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : savedInvoices.length === 0 ? (
              <Alert severity="info">No hay facturas guardadas</Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#1565C0' }}>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>No. Factura</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Cliente</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Tipo</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }} align="right">Total</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Estado</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }} align="center">Cobro</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Fecha</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600, width: 60 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {savedInvoices.map((inv) => (
                      <TableRow key={inv._id} hover sx={{ '&:nth-of-type(even)': { backgroundColor: '#F5F5F5' } }}>
                        <TableCell>
                          <Typography fontWeight={600}>{inv.invoiceNumber}</Typography>
                        </TableCell>
                        <TableCell>{inv.clientId?.name || '-'}</TableCell>
                        <TableCell>{TYPE_LABELS[inv.invoiceType] || inv.invoiceType}</TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={500}>${(inv.total || 0).toLocaleString()}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={inv.status}
                            size="small"
                            color={STATUS_COLORS[inv.status] || 'default'}
                          />
                        </TableCell>
                        {/* Cobrar ya mete la factura en caja y, si es de renovación, renueva los
                            equipos. Aquí solo se informa: era un checkbox y marcarlo o
                            desmarcarlo a mano sobre una factura cobrada no significaba nada. */}
                        <TableCell align="center">
                          {inv.status === 'paid' ? (
                            <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                              <Tooltip title={`Cobrada${inv.paidDate ? ' el ' + new Date(inv.paidDate).toLocaleDateString('es-DO') : ''}${METODO_PAGO[inv.paymentInfo?.method || ''] ? ' por ' + METODO_PAGO[inv.paymentInfo!.method!] : ''}${inv.paymentInfo?.transactionId ? ' · ref. ' + inv.paymentInfo.transactionId : ''} y contabilizada en caja`}>
                                <Chip label="PAGADA" size="small" color="success" sx={{ fontWeight: 700 }} />
                              </Tooltip>
                              {inv.paymentInfo?.evidenceUrl ? (
                                <Tooltip title="Ver el comprobante del pago">
                                  <IconButton size="small" onClick={() => window.open(urlDeArchivo(inv.paymentInfo?.evidenceUrl) || '', '_blank')}>
                                    <ReceiptIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              ) : (
                                <Tooltip title="Se cobró sin comprobante adjunto">
                                  <ReceiptIcon fontSize="small" sx={{ opacity: 0.25 }} />
                                </Tooltip>
                              )}
                            </Stack>
                          ) : inv.registeredInCash ? (
                            <Tooltip title="Ese dinero ya cuenta como ingreso del mes. No se saca de caja: si fue un error, anula la factura.">
                              <Chip label="EN CAJA" size="small" color="info" variant="outlined" />
                            </Tooltip>
                          ) : (
                            <Tooltip title="Abrir la factura para cerrarla con el pago: entra a caja y, si es renovación, renueva los equipos sola.">
                              <Button size="small" variant="outlined" color="success" onClick={() => cobrarDesdeLista(inv)} sx={{ py: 0, px: 1, fontSize: 12 }}>
                                Cobrar
                              </Button>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell>
                          {inv.issueDate ? new Date(inv.issueDate).toLocaleDateString('es-DO') : '-'}
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5}>
                            <IconButton size="small" color="primary" onClick={() => handleRedownload(inv)} title="Descargar PDF">
                              <PdfIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => abrirFactura(inv)}
                              title={inv.status === 'paid' ? 'Ver la factura (ya cobrada)' : 'Seguir trabajando esta factura'}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={inv.status === 'paid' || borrando === inv._id}
                              onClick={() => borrarFactura(inv)}
                              title={inv.status === 'paid' ? 'Una factura cobrada no se borra' : 'Borrar esta factura'}
                            >
                              {borrando === inv._id
                                ? <CircularProgress size={16} />
                                : <DeleteIcon fontSize="small" />}
                            </IconButton>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}
      {/* Cobrar cierra la factura para siempre y la mete en caja, así que se dice antes. */}
      <Dialog open={dialogoPago} onClose={() => !cobrando && setDialogoPago(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Cerrar la factura {invoiceNumber} con el pago</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Al cobrarla queda cerrada: <strong>no se puede editar ni devolver a pendiente</strong>, y
            entra sola a la contabilidad del mes. Si el monto está mal, corrígelo antes.
          </Alert>

          <Typography variant="h6" sx={{ mb: 2 }}>RD${total.toLocaleString('es-DO')}</Typography>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>¿Cómo pagaron?</InputLabel>
            <Select
              value={formaDePago.method}
              label="¿Cómo pagaron?"
              onChange={(e) => setFormaDePago({ ...formaDePago, method: e.target.value })}
            >
              <MenuItem value="cash">Efectivo</MenuItem>
              <MenuItem value="bank_transfer">Transferencia</MenuItem>
              <MenuItem value="check">Cheque</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="No. de transferencia o cheque (opcional)"
            value={formaDePago.transactionId}
            onChange={(e) => setFormaDePago({ ...formaDePago, transactionId: e.target.value })}
            sx={{ mb: 2 }}
          />

          <Button variant="outlined" component="label" fullWidth sx={{ mb: 1 }}>
            {recibo ? `Recibo: ${recibo.name}` : 'Adjuntar el recibo'}
            <input
              type="file"
              hidden
              accept="image/*,application/pdf"
              onChange={(e) => setRecibo(e.target.files?.[0] || null)}
            />
          </Button>
          <Typography variant="caption" color="text.secondary">
            {formaDePago.method === 'cash'
              ? 'En efectivo el recibo es opcional, pero si lo tienes adjúntalo.'
              : 'Obligatorio para transferencia y cheque: es lo que permite rastrear el cobro después.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogoPago(false)} disabled={cobrando}>Cancelar</Button>
          <Button
            variant="contained"
            color="success"
            onClick={cerrarConPago}
            disabled={cobrando || (formaDePago.method !== 'cash' && !recibo)}
            startIcon={cobrando ? <CircularProgress size={18} color="inherit" /> : <MoneyIcon />}
          >
            {cobrando ? 'Cobrando...' : 'Cobrar y cerrar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InvoiceGeneratorPage;
